import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { QRCodeSVG } from 'qrcode.react'
import {
  GiftIcon,
  QrCodeIcon,
  ClockIcon,
  ChevronRightIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline'
import LoadingSpinner from '@/components/LoadingSpinner'
import { useRewardsBalance, useRewardCoupons } from '@/hooks/useRewards'
import type { RewardCoupon } from '@/types'

// ---------------------------------------------------------------------------
// Rotating QR card (primary coupon displayed on the home rewards page)
// ---------------------------------------------------------------------------

interface RotatingQRCardProps {
  coupon: RewardCoupon
}

function RotatingQRCard({ coupon }: RotatingQRCardProps) {
  const { t } = useTranslation('rewards')
  const [secondsLeft, setSecondsLeft] = useState(coupon.expires_in_seconds)
  const [currentPayload, setCurrentPayload] = useState(coupon.qr_payload)
  const [prevPayload, setPrevPayload] = useState(coupon.qr_payload)
  const { refetch, isFetching } = useRewardCoupons()

  // Reset the countdown + shown payload when the coupon rotates (qr_payload
  // changes) — adjusting state during render (React-recommended) rather than
  // synchronously in an effect.
  if (prevPayload !== coupon.qr_payload) {
    setPrevPayload(coupon.qr_payload)
    setSecondsLeft(coupon.expires_in_seconds)
    setCurrentPayload(coupon.qr_payload)
  }

  // Count down; when the timer hits 0 re-fetch coupons so QR rotates
  useEffect(() => {
    if (secondsLeft <= 0) {
      void refetch()
      return
    }
    const id = window.setTimeout(() => setSecondsLeft((s) => s - 1), 1000)
    return () => window.clearTimeout(id)
  }, [secondsLeft, refetch])

  return (
    <div className="flat-card p-4 flex flex-col items-center gap-3">
      <p className="text-sm font-semibold text-base-content text-center">{coupon.campaign_name}</p>
      <p className="text-xs text-base-content/60 text-center">{t('campaign_qr')}</p>

      {/* QR code */}
      <div className="p-3 bg-white rounded-xl shadow-sm border border-base-200">
        {isFetching ? (
          <div className="w-[160px] h-[160px] flex items-center justify-center">
            <span className="text-xs text-base-content/50">{t('qr_refreshing')}</span>
          </div>
        ) : (
          <QRCodeSVG value={currentPayload} size={160} level="M" />
        )}
      </div>

      {/* Countdown */}
      <div className="flex items-center gap-1 text-xs text-base-content/60">
        <ClockIcon className="w-3.5 h-3.5" />
        <span>{t('qr_expires_in', { seconds: Math.max(0, secondsLeft) })}</span>
      </div>

      <Link to="/rewards/qr" className="btn btn-primary btn-sm w-full gap-1">
        <QrCodeIcon className="w-4 h-4" />
        {t('my_qr')}
      </Link>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function RewardsPage() {
  const { t } = useTranslation('rewards')
  const { t: tc } = useTranslation('common')
  const { data: balance, isLoading: balanceLoading, error: balanceError } = useRewardsBalance()
  const { data: coupons, isLoading: couponsLoading } = useRewardCoupons()

  if (balanceLoading || couponsLoading) {
    return <LoadingSpinner />
  }

  if (balanceError) {
    return (
      <div className="p-4">
        <div className="alert alert-error">{tc('errors.something_went_wrong')}</div>
      </div>
    )
  }

  const primaryCoupon = coupons?.[0] ?? null

  return (
    <div className="p-4 space-y-4 pb-4">
      {/* Points balance header */}
      <div className="bg-primary rounded-2xl p-5 text-primary-content shadow-md">
        <div className="flex items-center gap-2 mb-3">
          <SparklesIcon className="w-5 h-5" />
          <span className="text-sm font-semibold opacity-80">{t('points_balance')}</span>
        </div>
        <p className="text-4xl font-bold leading-none">
          {balance?.points_balance.toLocaleString() ?? '0'}
        </p>
        <p className="text-sm opacity-70 mt-0.5">{t('points_unit')}</p>

        <div className="mt-4 pt-3 border-t border-white/20 flex gap-6">
          <div>
            <p className="text-xs opacity-60">{t('lifetime_earned')}</p>
            <p className="text-sm font-semibold">
              {balance?.lifetime_earned.toLocaleString() ?? '0'}
            </p>
          </div>
          <div>
            <p className="text-xs opacity-60">{t('lifetime_redeemed')}</p>
            <p className="text-sm font-semibold">
              {balance?.lifetime_redeemed.toLocaleString() ?? '0'}
            </p>
          </div>
        </div>
      </div>

      {/* Rotating QR card (primary campaign) */}
      {primaryCoupon ? (
        <RotatingQRCard coupon={primaryCoupon} />
      ) : (
        <div className="flat-card p-5 flex flex-col items-center gap-3 text-center">
          <div className="icon-container">
            <QrCodeIcon className="w-5 h-5" />
          </div>
          <p className="text-sm font-medium text-base-content">{t('no_coupons')}</p>
          <p className="text-xs text-base-content/50">{t('no_coupons_sub')}</p>
        </div>
      )}

      {/* Navigation links */}
      <Link to="/rewards/catalog" className="flat-card list-item-row block">
        <div className="flex items-center gap-3">
          <div className="icon-container">
            <GiftIcon className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-base-content">{t('view_catalog')}</p>
          </div>
          <ChevronRightIcon className="w-4 h-4 text-base-content/40 flex-shrink-0" />
        </div>
      </Link>

      <Link to="/rewards/redemptions" className="flat-card list-item-row block">
        <div className="flex items-center gap-3">
          <div className="icon-container">
            <ClockIcon className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-base-content">{t('view_history')}</p>
          </div>
          <ChevronRightIcon className="w-4 h-4 text-base-content/40 flex-shrink-0" />
        </div>
      </Link>
    </div>
  )
}
